import React from 'react';
import { View, StyleSheet, TouchableOpacity, useWindowDimensions, TextStyle, ViewStyle, ImageStyle } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Typography } from '../ui';
import { BrandLogo } from '../BrandLogo';
import { fonts } from '../../styles';
import { useTheme } from '../../context/ThemeContext';
import { useI18n } from '../../i18n';
import { openApp } from './openApp';
import { openCookiePreferences } from '../cookiePreferences';
import { landingLayout } from './constants';
import { fontWeights } from '../../styles/typographyTokens';
import { visibleFooterSocials, type SocialNetworkId } from '../../config/socialLinks';
import { useFooterHiddenSocialsShown } from '../../hooks/useFeatureFlags';
import { useForeignJurisdiction, useLegalEntityLine } from '../../legal/useLegalEntityLine';
import { shownFooterLinks } from '../../legal/docIds';

// CORE-1659 — ссылка без `path` открывает окно «Настройки cookie», а не страницу.
type FooterLink = { labelKey: string; path?: string };
type FooterGroup = { titleKey: string; links: FooterLink[] };
type SocialItem = { id?: SocialNetworkId; icon: keyof typeof Ionicons.glyphMap; label: string };

const GROUPS: FooterGroup[] = [
  {
    titleKey: 'navigation',
    links: [
      { labelKey: 'practices', path: '/practices' },
      { labelKey: 'calendar', path: '/lives' },
      { labelKey: 'masters', path: '/masters' },
      { labelKey: 'quickRooms', path: '/dashboard' },
    ],
  },
  {
    titleKey: 'platform',
    links: [
      { labelKey: 'about', path: '/about' },
    ],
  },
  {
    titleKey: 'legal',
    links: [
      { labelKey: 'privacyPolicy', path: '/legal/privacy-policy' },
      { labelKey: 'cookieSettings' },
      { labelKey: 'termsOfService', path: '/legal/terms-of-use' },
      { labelKey: 'streamingAgreement', path: '/legal/streaming-agreement' },
    ],
  },
];

// CORE-1768 — Instagram скрывается общим списком `FOOTER_HIDDEN_SOCIALS`, а не
// по стране: с VPN региональное правило (CORE-107) не держалось.
const SOCIALS: SocialItem[] = [
  { id: 'instagram', icon: 'logo-instagram', label: 'Instagram' },
  { id: 'telegram', icon: 'paper-plane-outline', label: 'Telegram' },
  { id: 'youtube', icon: 'logo-youtube', label: 'YouTube' },
  { icon: 'radio-outline', label: 'Live' },
];

const normalizePath = (path: string) => {
  if (path === '/practice') return '/practices';
  return path;
};

export const Footer: React.FC = () => {
  const { width } = useWindowDimensions();
  const { colors: themeColors, isDark } = useTheme();
  const { t } = useI18n('landing');
  const isMobile = width < 768;
  const socials = visibleFooterSocials(SOCIALS, useFooterHiddenSocialsShown());
  const legalEntityLine = useLegalEntityLine();
  // CORE-1567 — Streaming Agreement (AGAPE.IO Limited) — только вне контура РФ.
  const foreignJurisdiction = useForeignJurisdiction();

  return (
    <View style={[styles.footer, { backgroundColor: themeColors.pageBg, borderTopColor: isDark ? themeColors.border : '#F0E7DE' }]}>
      <View style={[styles.container, isMobile && styles.containerMobile]}>
        <View style={[styles.grid, isMobile && styles.gridMobile]}>
          <View style={[styles.brandCol, isMobile && styles.colMobile]}>
            <BrandLogo height={42} style={{ marginBottom: 22 }} />
            <Typography style={[styles.brandText, { color: themeColors.textSecondary }]}>
              {t('footerV2.description1')}
            </Typography>
            <Typography style={[styles.brandText, { color: themeColors.textSecondary }]}>
              {t('footerV2.description2')}
            </Typography>
            <Typography style={[styles.brandStrong, { color: themeColors.textPrimary }]}>{t('footerV2.tagline')}</Typography>
            <View style={styles.socialRow}>
              {socials.map((social) => (
                <TouchableOpacity key={social.label} activeOpacity={0.75} style={styles.socialBtn} accessibilityLabel={social.label}>
                  <Ionicons name={social.icon} size={18} color={themeColors.textPrimary} />
                </TouchableOpacity>
              ))}
            </View>
            {/* CORE-1934 — та же строка реквизитов, что в подвале приложения. */}
            <Typography style={[styles.legalEntity, { color: themeColors.textSecondary }]}>
              {legalEntityLine}
            </Typography>
          </View>

          {GROUPS.map((group) => (
            <View key={group.titleKey} style={[styles.linkGroup, isMobile && styles.colMobile]}>
              <Typography style={[styles.groupTitle, { color: themeColors.textPrimary }]}>{t(`footerV2.groups.${group.titleKey}`)}</Typography>
              {shownFooterLinks(group.links, foreignJurisdiction).map((link) => (
                <TouchableOpacity
                  key={link.labelKey}
                  testID={`footer-link-${link.labelKey}`}
                  activeOpacity={0.72}
                  onPress={() => (link.path ? openApp(normalizePath(link.path)) : openCookiePreferences())}
                >
                  <Typography style={[styles.linkText, { color: themeColors.textSecondary }]}>{t(`footerV2.links.${link.labelKey}`)}</Typography>
                </TouchableOpacity>
              ))}
            </View>
          ))}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  footer: {
    backgroundColor: '#FFFFFF',
    borderTopWidth: 1,
    borderTopColor: '#F0E7DE',
    paddingTop: 38,
    paddingBottom: 42,
  } as ViewStyle,
  container: {
    maxWidth: landingLayout.maxWidth,
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: landingLayout.paddingH,
  } as ViewStyle,
  containerMobile: {
    paddingHorizontal: landingLayout.paddingH,
  } as ViewStyle,
  grid: {
    flexDirection: 'row',
    gap: 62,
    alignItems: 'flex-start',
  } as ViewStyle,
  gridMobile: {
    flexDirection: 'column',
    gap: 34,
  } as ViewStyle,
  colMobile: {
    flex: 0,
    flexBasis: 'auto',
    flexShrink: 0,
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
  } as ViewStyle,
  brandCol: {
    flex: 1.4,
    minWidth: 260,
    maxWidth: 420,
  } as ViewStyle,
  logo: {
    width: 154,
    height: 42,
    marginBottom: 22,
  } as ImageStyle,
  brandText: {
    color: '#5C5652',
    fontSize: 14,
    lineHeight: 22,
    fontWeight: '500',
    marginBottom: 12,
  } as TextStyle,
  brandStrong: {
    color: '#1C1713',
    fontSize: 15,
    lineHeight: 22,
    fontWeight: fontWeights.bold,
    marginTop: 2,
  } as TextStyle,
  socialRow: {
    flexDirection: 'row',
    gap: 16,
    marginTop: 18,
  } as ViewStyle,
  legalEntity: {
    color: '#5C5652',
    fontSize: 12,
    lineHeight: 18,
    fontWeight: '500',
    marginTop: 18,
  } as TextStyle,
  socialBtn: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  } as ViewStyle,
  linkGroup: {
    flex: 1,
    minWidth: 190,
    gap: 12,
  } as ViewStyle,
  groupTitle: {
    color: '#1C1713',
    fontFamily: fonts.display,
    fontSize: 15,
    lineHeight: 22,
    fontWeight: fontWeights.bold,
    marginBottom: 6,
  } as TextStyle,
  linkText: {
    color: '#5C5652',
    fontSize: 14,
    lineHeight: 22,
    fontWeight: '600',
  } as TextStyle,
});
